import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, ChevronRight, Zap, Trophy } from 'lucide-react'
import { rankForLevel } from '../lib/ranks'

// Dữ liệu màn "qua ải". level/xp* có thể đến SAU (sau khi server trả) → cập nhật mượt.
export interface QuestClearData {
  cardIndex: number
  cardTitle: string
  hasQ: boolean
  stars: number          // 0..3
  xpGain: number
  isLast: boolean
  accent: string
  level?: number
  xpInLevel?: number
  xpForNextLevel?: number
  leveledUp?: boolean
}

interface Props {
  data: QuestClearData | null
  fx: boolean
  onContinue: () => void
}

// Màn ăn mừng khi vượt 1 ải: đóng băng nền, sao hiện lần lượt, +XP, thanh cấp độ chạy.
const QuestClearModal: React.FC<Props> = ({ data, fx, onContinue }) => {
  const [shownStars, setShownStars] = useState(0)

  useEffect(() => {
    if (!data) { setShownStars(0); return }
    const total = data.hasQ ? data.stars : 0
    if (total <= 0) return
    setShownStars(0)
    const timers = Array.from({ length: total }, (_, k) =>
      window.setTimeout(() => setShownStars(k + 1), 250 + k * (fx ? 280 : 120)),
    )
    return () => timers.forEach(clearTimeout)
  }, [data, fx])

  if (!data) return null
  const perfect = data.hasQ && data.stars >= 3
  const rank = data.level ? rankForLevel(data.level) : null
  const pct = data.xpForNextLevel ? Math.min(100, Math.round(((data.xpInLevel || 0) / data.xpForNextLevel) * 100)) : 0

  return (
    <AnimatePresence>
      {data && (
        <motion.div
          className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden text-center"
            initial={{ scale: fx ? 0.7 : 0.95, y: fx ? 30 : 0, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={fx ? { type: 'spring', stiffness: 320, damping: 22 } : { duration: 0.15 }}
          >
            {/* Dải màu đỉnh */}
            <div className="h-2" style={{ background: data.accent }} />
            <div className="px-6 pt-5 pb-6">
              <div className="text-xs font-black uppercase tracking-widest" style={{ color: data.accent }}>
                {perfect ? 'Hoàn hảo!' : 'Qua ải!'}
              </div>
              <h3 className="mt-1 text-lg font-black text-gray-900 leading-snug">{data.cardTitle}</h3>

              {/* Sao hiện lần lượt (chỉ ải có câu hỏi) */}
              {data.hasQ && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  {[1, 2, 3].map((n) => {
                    const on = n <= shownStars
                    return (
                      <motion.span key={n}
                        initial={{ scale: 0, rotate: -30 }}
                        animate={on ? { scale: 1, rotate: 0 } : { scale: 0.6, rotate: 0 }}
                        transition={fx ? { type: 'spring', stiffness: 400, damping: 12 } : { duration: 0.1 }}
                      >
                        <Star size={42} className={on ? 'text-amber-400 fill-amber-400 drop-shadow' : 'text-gray-200'} />
                      </motion.span>
                    )
                  })}
                </div>
              )}

              {/* +XP */}
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 font-black">
                <Zap size={16} className="fill-violet-400 text-violet-500" /> +{data.xpGain} XP
              </div>

              {/* Cấp bậc + thanh tiến độ (khi đã đăng nhập, server trả level) */}
              {rank && (
                <div className="mt-4">
                  {data.leveledUp && (
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 14 }}
                      className="mb-2 text-sm font-black text-amber-500"
                    >
                      ⬆ LÊN CẤP {data.level}!
                    </motion.div>
                  )}
                  <div className="flex items-center justify-center gap-1.5 text-sm font-bold" style={{ color: rank.color }}>
                    <span className="text-base">{rank.icon}</span> {rank.name} · Cấp {data.level}
                  </div>
                  <div className="mt-2 h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
                    <motion.div className="h-full rounded-full"
                      style={{ background: rank.color }}
                      initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                      transition={{ duration: fx ? 0.9 : 0.2, ease: 'easeOut' }}
                    />
                  </div>
                  <div className="mt-1 text-[11px] text-gray-400">{data.xpInLevel}/{data.xpForNextLevel} XP tới cấp sau</div>
                </div>
              )}

              <button
                onClick={onContinue}
                className="mt-5 w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl text-white font-black shadow-md hover:brightness-110"
                style={{ background: data.accent }}
              >
                {data.isLast ? <>Tổng kết phần <Trophy size={18} /></> : <>Ải tiếp theo <ChevronRight size={18} /></>}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default QuestClearModal
